import { Body, Box, Button, Heading } from '@hover/blueprint';
import { iconLock } from '@hover/icons';
import React, { useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { EmailMeALinkButton } from 'src/modules/onboarding/components/MagicLinkButton';
import { Link } from 'src/shared/components/Link';
import {
  TranslationNamespace,
  useEffectOnMount,
  useTranslate,
} from 'src/shared/hooks';
import { Route as Routes } from 'src/shared/route';
import {
  buttonImpression,
  buttonPressed,
  pageViewed,
} from 'src/shared/telemetry';

export const Welcome = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const { search } = useLocation();

  // Integrations will get pushed to this route when authorizing. We want to make sure a user
  // cannot use Magic Link when trying to authorize an integration. This is because
  // integrations sometimes need to be authorized in a different browser. This is the case
  // for Postman or Sketchup for example.
  const searchParams = new URLSearchParams(search);
  const redirectToParam = searchParams.get('redirect_to');
  const isIntegration =
    redirectToParam &&
    decodeURIComponent(redirectToParam)
      .toLowerCase()
      .includes('response_type=code');

  const pageOrScreenName = 'Login_input';
  const magicLinkUrl = `${Routes.SIGN_UP_ONBOARDING.path}/login${search}`;

  const trackButtonImpression = useCallback(
    (buttonText: string) => {
      buttonImpression({
        page_or_screen_name: pageOrScreenName,
        feature: 'onboarding',
        button_text: buttonText,
      });
    },
    [pageOrScreenName],
  );

  useEffect(() => {
    trackButtonImpression('Email me a link');
    trackButtonImpression('Continue with password');
  }, [trackButtonImpression]);

  const shouldRenderMagicLink = !isIntegration;

  const history = useHistory();

  useEffectOnMount(() => {
    pageViewed({
      page_or_screen_name: pageOrScreenName,
      feature: 'onboarding',
    });
  });

  return (
    <Box
      alignItems="center"
      height="100dvh"
      justifyContent="center"
      paddingX={{ base: 500, tablet: 600 }}
    >
      <Box flexDirection="column">
        <Heading
          color="text.primary"
          size={{ base: 700, tablet: 900 }}
          testId="welcome-heading"
          textAlign="center"
        >
          {t('onboarding.welcomeBack')}
        </Heading>
        <Button
          aria-label={t('onboarding.continueWithPassword')}
          fill="outline"
          iconBefore={iconLock}
          mb={300}
          onClick={() => {
            buttonPressed({
              button_text: 'Continue with Password',
              page_or_screen_name: pageOrScreenName,
              feature: 'onboarding',
            });
            const link = `${Routes.SIGN_UP_ONBOARDING.path}/password${search}`;
            // FIXME: we need to replace it to use the prop to, actually it's not working properly.
            history.push(link);
          }}
          p={400}
          size="large"
          testId="continue-with-password"
          textDecoration="none"
          variant="secondary"
        >
          <Body>{t('onboarding.continueWithPassword')}</Body>
        </Button>

        {shouldRenderMagicLink && (
          <EmailMeALinkButton
            pageOrScreenName={pageOrScreenName}
            to={magicLinkUrl}
          />
        )}
        <Body m={400} testId="use-sso" textAlign="center">
          <Link
            color="neutral.700"
            onClick={() =>
              buttonPressed({
                button_text: 'Use Single Sign On',
                page_or_screen_name: pageOrScreenName,
                feature: 'onboarding',
              })
            }
            textDecoration="underline"
            to={`${Routes.SIGN_UP_ONBOARDING.path}/login/sso${search}`}
          >
            {t('onboarding.useSSO')}
          </Link>
        </Body>
        <Body testId="signup-login-link" textAlign="center">
          {t('onboarding.dontHaveAcc')}
          <Link
            color="neutral.700"
            ml={100}
            onClick={() =>
              buttonPressed({
                button_text: 'Sign up',
                page_or_screen_name: pageOrScreenName,
                feature: 'onboarding',
              })
            }
            textDecoration="underline"
            to={`${Routes.SIGN_UP_ONBOARDING.path}/start${search}`}
          >
            {t('onboarding.signup')}
          </Link>
        </Body>
      </Box>
    </Box>
  );
};
